// Audio helpers built on one shared AudioContext: level meters, speaking detection and test tones.

let ctx: AudioContext | null = null;

export function audioContext(): AudioContext {
  ctx ??= new AudioContext();
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** Reads the RMS level (0..1) of an audio track. */
export class LevelMeter {
  private analyser: AnalyserNode;
  private source: MediaStreamAudioSourceNode;
  private buffer: Float32Array<ArrayBuffer>;

  constructor(track: MediaStreamTrack) {
    const ac = audioContext();
    this.source = ac.createMediaStreamSource(new MediaStream([track]));
    this.analyser = ac.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.smoothingTimeConstant = 0.4;
    this.buffer = new Float32Array(this.analyser.fftSize);
    this.source.connect(this.analyser);
  }

  level(): number {
    this.analyser.getFloatTimeDomainData(this.buffer);
    let sum = 0;
    for (const v of this.buffer) sum += v * v;
    const rms = Math.sqrt(sum / this.buffer.length);
    return Math.min(1, rms * 4);
  }

  dispose(): void {
    this.source.disconnect();
    this.analyser.disconnect();
  }
}

/** Plays a short two-note chime on the chosen output so the user can check their speakers. */
export async function playTestSound(sinkId: string): Promise<void> {
  const ac = new AudioContext();
  const dest = ac.createMediaStreamDestination();
  const gain = ac.createGain();
  gain.connect(dest);
  const notes = [523.25, 659.25, 783.99];
  notes.forEach((freq, i) => {
    const osc = ac.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const t = ac.currentTime + i * 0.22;
    const g = ac.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.25, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    osc.connect(g).connect(gain);
    osc.start(t);
    osc.stop(t + 0.55);
  });
  const el = new Audio();
  el.srcObject = dest.stream;
  if (sinkId && sinkId !== 'default' && 'setSinkId' in el) await (el as HTMLAudioElement & { setSinkId(id: string): Promise<void> }).setSinkId(sinkId);
  await el.play();
  await new Promise((r) => setTimeout(r, 1300));
  el.pause();
  await ac.close();
}

/** Soft notification blip for incoming chat messages. */
export function playBlip(): void {
  try {
    const ac = audioContext();
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ac.currentTime);
    g.gain.exponentialRampToValueAtTime(0.08, ac.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.18);
    osc.connect(g).connect(ac.destination);
    osc.start();
    osc.stop(ac.currentTime + 0.2);
  } catch {
    /* autoplay restrictions: silently skip */
  }
}
